<template>
  <div>
    <Card v-if="propertyId === 'create'" icon="mdi-home-plus" title="Create Property">
      <DynamicForm
        :key="propertyId"
        :object="createPropertyObject"
        :fields="propertyFields"
        :save-method="(data) => $api.rpc('property', 'createProperty', {propertyHolderId: propertyHolder.id, property: data})"
        :success-callback="(item) => $router.push('/h/' + household.id + '/property/' + propertyHolder.id + '/' + item.id)"
        :focus-index="0"
      />
    </Card>

    <div v-else-if="propertyId === 'settings' && propertyHolder">
      <Card icon="mdi-home-city" :title="propertyHolder.name">
        <EditableObject
          :object="propertyHolder"
          :fields="[{fieldName:'name', displayName:'Name', type:String, editable:true, rules:[rules.required]}]"
          :saveMethod="(o) => $api.rpc('property', 'savePropertyHolder', {propertyHolder: o})"
        />
        <br/>
        <ButtonWithConfirmation
          :confirmation-text='"Are you sure you want to delete \"" + propertyHolder.name + "\"?"'
          confirmation-icon="mdi-delete"
          button-text="Delete"
          button-color="error"
          :action="() => $api.rpc('property', 'deletePropertyHolder', {propertyHolder})"
          :success-callback="() => $router.push('/h/' + household.id)"
        />
      </Card>
      <Permissions :id="propertyHolder.id" type="propertyholder" showHeaderDivider/>
    </div>

    <div v-else-if="selectedProperty">
      <Card icon="mdi-home" :title="selectedProperty.name" wide>
        <v-row dense class="mb-4">
          <v-col cols="12" md="4">
            <div class="summary-label">Address</div>
            <div>{{ propertyAddress(selectedProperty) }}</div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="summary-label">Current Value</div>
            <div>{{ currentPropertyValue ? formatMoney(currentPropertyValue.value) + ' (' + currentPropertyValue.date + ')' : 'No value recorded' }}</div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="summary-label">Next Maintenance</div>
            <div>{{ nextPropertyMaintenance ? nextPropertyMaintenance.option.name + ' - ' + (nextPropertyMaintenance.nextDueDate || 'No due date') : 'No maintenance options yet' }}</div>
          </v-col>
        </v-row>
        <v-row dense class="mb-4">
          <v-col cols="12" md="6">
            <div class="summary-label">Zillow Property ID</div>
            <div>{{ selectedProperty.zillowPropertyId || 'Not set' }}</div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="summary-label">Value Link</div>
            <a v-if="selectedProperty.valueUrl" :href="selectedProperty.valueUrl" target="_blank" rel="noopener noreferrer">{{ selectedProperty.valueUrl }}</a>
            <div v-else>Not set</div>
          </v-col>
        </v-row>
        <v-expansion-panels v-model="propertyEditorPanel" flat class="property-edit-panel">
          <v-expansion-panel>
            <v-expansion-panel-header>Edit Property Details</v-expansion-panel-header>
            <v-expansion-panel-content>
              <DynamicForm
                :key="selectedProperty.id + ':' + propertyEditorPanel"
                :object="selectedProperty"
                :fields="propertyFields"
                :save-method="(o) => $api.rpc('property', 'saveProperty', {property: o})"
                :delete-method="() => $api.rpc('property', 'deleteProperty', {property: selectedProperty})"
                :cancel-callback="() => { propertyEditorPanel = null }"
                :success-callback="(response) => {
                  propertyEditorPanel = null
                  if (!response)
                    $router.push('/h/' + household.id + '/property/' + propertyHolder.id)
                }"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </Card>

      <Card icon="mdi-cash" title="Value" wide :actions="[{icon:'mdi-plus', color:'primary', click:() => openValueDialog()}]">
        <v-row dense>
          <v-col cols="12" md="4">
            <v-currency-field v-model="todayValue" label="Today's Value" outlined dense prefix="$"/>
          </v-col>
          <v-col cols="12" md="4">
            <v-btn color="primary" @click="saveTodayValue">Save Today's Value</v-btn>
          </v-col>
        </v-row>
        <v-data-table :items="values" :headers="valueHeaders" hide-default-footer disable-pagination>
          <template v-slot:item.date="{ item }">{{ item.date }}</template>
          <template v-slot:item.value="{ item }">{{ formatMoney(item.value) }}</template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small @click="openValueDialog(item)"><v-icon>mdi-pencil</v-icon></v-btn>
            <v-btn icon small @click="deleteValue(item)"><v-icon>mdi-delete</v-icon></v-btn>
          </template>
        </v-data-table>
      </Card>

      <Card icon="mdi-tools" title="Maintenance Options" wide :actions="[{icon:'mdi-plus', color:'primary', click:() => openMaintenanceOptionDialog()}]">
        <v-data-table :items="maintenanceOptions" :headers="maintenanceOptionHeaders" hide-default-footer disable-pagination>
          <template v-slot:item.name="{ item }">{{ item.option.name }}</template>
          <template v-slot:item.schedule="{ item }">{{ formatScheduleMode(item.option.scheduleMode) }}</template>
          <template v-slot:item.interval="{ item }">Every {{ item.option.intervalMonths }} months</template>
          <template v-slot:item.lastCompletedDate="{ item }">{{ item.lastCompletedDate || '-' }}</template>
          <template v-slot:item.nextDueDate="{ item }">{{ item.nextDueDate || '-' }}</template>
          <template v-slot:item.overdue="{ item }">
            <span :class="item.overdue ? 'error--text' : ''">{{ item.overdue ? 'Overdue' : 'OK' }}</span>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small @click="openMaintenanceOptionDialog(item.option)"><v-icon>mdi-pencil</v-icon></v-btn>
            <v-btn icon small @click="deleteMaintenanceOption(item.option)"><v-icon>mdi-delete</v-icon></v-btn>
          </template>
        </v-data-table>
      </Card>

      <Card icon="mdi-wrench" title="Maintenance History" wide :actions="[{icon:'mdi-plus', color:'primary', click:() => openMaintenanceEventDialog()}]">
        <v-data-table :items="maintenanceEvents" :headers="maintenanceEventHeaders" hide-default-footer disable-pagination>
          <template v-slot:item.option="{ item }">{{ optionName(item.propertyMaintenanceOptionId) }}</template>
          <template v-slot:item.cost="{ item }">{{ item.cost ? formatMoney(item.cost) : '-' }}</template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small @click="openMaintenanceEventDialog(item)"><v-icon>mdi-pencil</v-icon></v-btn>
            <v-btn icon small @click="deleteMaintenanceEvent(item)"><v-icon>mdi-delete</v-icon></v-btn>
          </template>
        </v-data-table>
      </Card>
    </div>

    <Card v-else-if="properties && properties.length === 0" :title="'Welcome to ' + propertyHolder.name">
      <span class="subtitle-1">
        Create your first property by clicking
        <v-btn rounded color="primary" :to="'/h/' + household.id + '/property/' + propertyHolder.id + '/create'"><v-icon left>mdi-plus</v-icon>Create Property</v-btn>
      </span>
    </Card>

    <div v-else>
      <Card v-for="property in properties" :key="property.id" icon="mdi-home" :title="property.name" :to="'/h/' + household.id + '/property/' + propertyHolder.id + '/' + property.id">
        <div>{{ property.description || 'No description yet.' }}</div>
        <div class="property-preview mt-2">{{ propertyAddress(property) }}</div>
      </Card>
    </div>

    <v-dialog v-model="showValueDialog" max-width="640">
      <Card title="Save Value" icon="mdi-cash" inDialog>
        <DynamicForm
          :key="showValueDialog + ':' + valueDialogObject.date"
          :object="valueDialogObject"
          :fields="valueDialogFields"
          :save-method="saveValueDialog"
          :cancel-callback="() => showValueDialog = false"
          :success-callback="() => { showValueDialog = false; loadPropertyData(); }"
          :focus-index="0"
        />
      </Card>
    </v-dialog>

    <v-dialog v-model="showMaintenanceOptionDialog" max-width="720">
      <Card title="Maintenance Option" icon="mdi-tools" inDialog>
        <DynamicForm
          :key="showMaintenanceOptionDialog + ':' + maintenanceOptionDialogObject.id"
          :object="maintenanceOptionDialogObject"
          :fields="maintenanceOptionDialogFields"
          :save-method="saveMaintenanceOptionDialog"
          :cancel-callback="() => showMaintenanceOptionDialog = false"
          :success-callback="() => { showMaintenanceOptionDialog = false; loadPropertyData(); }"
          @templateKeychange="applyPropertyTemplate"
          :focus-index="0"
        />
      </Card>
    </v-dialog>

    <v-dialog v-model="showMaintenanceEventDialog" max-width="720">
      <Card title="Maintenance" icon="mdi-wrench" inDialog>
        <DynamicForm
          :key="showMaintenanceEventDialog + ':' + maintenanceEventDialogObject.id"
          :object="maintenanceEventDialogObject"
          :fields="maintenanceEventDialogFields"
          :save-method="saveMaintenanceEventDialog"
          :cancel-callback="() => showMaintenanceEventDialog = false"
          :success-callback="() => { showMaintenanceEventDialog = false; loadPropertyData(); }"
          :focus-index="0"
        />
      </Card>
    </v-dialog>
  </div>
</template>

<script>
import Card from '@/components/Card'
import DynamicForm from '@/components/DynamicForm'
import EditableObject from '@/components/EditableObject'
import ButtonWithConfirmation from '@/components/ButtonWithConfirmation'
import Permissions from '@/components/Permissions'
import { MaintenanceScheduleMode } from '@/api'

export default {
  name: 'PropertyHolder',
  components: { Permissions, ButtonWithConfirmation, EditableObject, DynamicForm, Card },
  props: ['household', 'propertyHolderId', 'propertyId'],
  data: () => ({
    subscription: null,
    propertyHolder: null,
    properties: [],
    maintenanceTemplates: [],
    maintenanceOptions: [],
    maintenanceEvents: [],
    values: [],
    todayValue: null,
    showValueDialog: false,
    valueDialogObject: { date: '', value: null },
    propertyEditorPanel: null,
    showMaintenanceOptionDialog: false,
    maintenanceOptionDialogObject: {},
    showMaintenanceEventDialog: false,
    maintenanceEventDialogObject: {},
    rules: {
      required: value => !!value || 'Required',
      max255: value => !value || value.length <= 255 || 'Must be 255 characters or less',
      positiveInt: value => value == null || (Number.isInteger(Number(value)) && Number(value) > 0) || 'Must be a whole number greater than 0',
      nonNegative: value => value == null || Number(value) >= 0 || 'Cannot be negative',
      validUrl: value => {
        if (!value)
          return true
        try {
          const parsed = new URL(value)
          return (!!parsed.protocol && !!parsed.host) || 'Must be a valid absolute URL'
        } catch (e) {
          return 'Must be a valid absolute URL'
        }
      }
    },
    valueHeaders: [
      { text: 'Date', value: 'date' },
      { text: 'Value', value: 'value' },
      { text: '', value: 'actions', sortable: false }
    ],
    maintenanceOptionHeaders: [
      { text: 'Name', value: 'name' },
      { text: 'Schedule', value: 'schedule' },
      { text: 'Interval', value: 'interval' },
      { text: 'Last Done', value: 'lastCompletedDate' },
      { text: 'Next Due', value: 'nextDueDate' },
      { text: 'Status', value: 'overdue' },
      { text: '', value: 'actions', sortable: false }
    ],
    maintenanceEventHeaders: [
      { text: 'Date', value: 'date' },
      { text: 'Option', value: 'option' },
      { text: 'Cost', value: 'cost' },
      { text: 'Notes', value: 'notes' },
      { text: '', value: 'actions', sortable: false }
    ]
  }),
  computed: {
    selectedProperty () {
      return this.properties.find(property => property.id === this.propertyId)
    },
    currentPropertyValue () {
      return this.values.length > 0 ? this.values[0] : null
    },
    nextPropertyMaintenance () {
      if (!this.maintenanceOptions || this.maintenanceOptions.length === 0)
        return null
      return [...this.maintenanceOptions].sort((a, b) => {
        if (a.overdue && !b.overdue)
          return -1
        if (!a.overdue && b.overdue)
          return 1
        return (a.nextDueDate || '9999-99-99').localeCompare(b.nextDueDate || '9999-99-99')
      })[0]
    },
    propertyFields () {
      return [
        { fieldName: 'name', displayName: 'Name', type: String, editable: true, rules: [this.rules.required, this.rules.max255], maxlength: 255 },
        { fieldName: 'description', displayName: 'Description', type: String, editable: true, rules: [this.rules.max255], maxlength: 255 },
        { fieldName: 'addressStreet1', displayName: 'Street 1', type: String, editable: true, rules: [this.rules.max255], maxlength: 255 },
        { fieldName: 'addressStreet2', displayName: 'Street 2', type: String, editable: true, rules: [this.rules.max255], maxlength: 255 },
        { fieldName: 'addressCity', displayName: 'City', type: String, editable: true, rules: [this.rules.max255], maxlength: 255 },
        { fieldName: 'addressState', displayName: 'State', type: String, editable: true, rules: [this.rules.max255], maxlength: 255 },
        { fieldName: 'addressPostalCode', displayName: 'Postal Code', type: String, editable: true, rules: [this.rules.max255], maxlength: 255 },
        { fieldName: 'addressCountry', displayName: 'Country', type: String, editable: true, rules: [this.rules.max255], maxlength: 255 },
        { fieldName: 'zillowPropertyId', displayName: 'Zillow Property ID', type: String, editable: true, rules: [this.rules.max255], maxlength: 255 },
        { fieldName: 'valueUrl', displayName: 'Value URL', type: String, editable: true, rules: [this.rules.max255, this.rules.validUrl], maxlength: 255 }
      ]
    },
    createPropertyObject () {
      return {
        name: '',
        description: '',
        addressStreet1: '',
        addressStreet2: '',
        addressCity: '',
        addressState: '',
        addressPostalCode: '',
        addressCountry: '',
        zillowPropertyId: '',
        valueUrl: ''
      }
    },
    valueDialogFields () {
      return [
        { fieldName: 'date', displayName: 'Date', type: 'LocalDate', small: true, rules: [this.rules.required] },
        { fieldName: 'value', displayName: 'Value', type: 'Currency', rules: [this.rules.required, this.rules.nonNegative] }
      ]
    },
    maintenanceOptionDialogFields () {
      return [
        { fieldName: 'templateKey', displayName: 'Template', type: 'Enumerator', values: this.maintenanceTemplates.map(item => ({ text: item.name, value: item.key })), clearable: true },
        { fieldName: 'name', displayName: 'Name', type: String, rules: [this.rules.required, this.rules.max255], maxlength: 255 },
        { fieldName: 'scheduleMode', displayName: 'Schedule Mode', type: 'Enumerator', values: this.scheduleModes, rules: [this.rules.required] },
        { fieldName: 'intervalMonths', displayName: 'Interval Months', type: Number, rules: [this.rules.required, this.rules.positiveInt] },
        { fieldName: 'startDate', displayName: 'Start Date', type: 'LocalDate', small: true, rules: [this.rules.required] }
      ]
    },
    maintenanceEventDialogFields () {
      return [
        { fieldName: 'date', displayName: 'Date', type: 'LocalDate', small: true, rules: [this.rules.required] },
        { fieldName: 'propertyMaintenanceOptionId', displayName: 'Maintenance Option', type: 'Enumerator', values: this.maintenanceOptions.map(item => ({ text: item.option.name, value: item.option.id })), rules: [this.rules.required] },
        { fieldName: 'cost', displayName: 'Cost', type: 'Currency', rules: [this.rules.nonNegative] },
        { fieldName: 'notes', displayName: 'Notes', type: 'LargeTextBox' }
      ]
    },
    scheduleModes () {
      return [
        { text: 'Fixed Schedule', value: 'FIXED_SCHEDULE' },
        { text: 'Reset From Completion', value: 'RESET_FROM_COMPLETION' }
      ]
    }
  },
  methods: {
    resubscribe () {
      if (this.subscription)
        this.$api.unsubscribe(this.subscription)
      this.subscription = this.$api.subscribe('UIPropertyHolder', this.propertyHolderId, holder => {
        this.propertyHolder = holder.propertyHolder
        this.properties = holder.properties || []
        this.loadPropertyData()
        this.updateMenu()
        this.updateBreadcrumbs()
      })
    },
    updateMenu () {
      if (!this.propertyHolder)
        return
      const menuItems = [{
        title: this.propertyHolder.name,
        icon: 'mdi-home-city',
        to: '/h/' + this.household.id + '/property/' + this.propertyHolder.id,
        color: '#8B5E34'
      }]
      this.properties.forEach(property => {
        menuItems.push({
          title: property.name,
          icon: 'mdi-home',
          to: '/h/' + this.household.id + '/property/' + this.propertyHolder.id + '/' + property.id
        })
      })
      menuItems.push({ title: 'Create Property', icon: 'mdi-plus', to: '/h/' + this.household.id + '/property/' + this.propertyHolder.id + '/create' })
      menuItems.push({ title: 'Settings', icon: 'mdi-cog', to: '/h/' + this.household.id + '/property/' + this.propertyHolder.id + '/settings' })
      this.$emit('menuItems', menuItems)
    },
    updateBreadcrumbs () {
      if (!this.propertyHolder)
        return
      const breadcrumbs = [{ text: this.propertyHolder.name, icon: 'mdi-home-city', to: '/h/' + this.household.id + '/property/' + this.propertyHolder.id }]
      if (this.selectedProperty)
        breadcrumbs.push({ text: this.selectedProperty.name, icon: 'mdi-home', to: '/h/' + this.household.id + '/property/' + this.propertyHolder.id + '/' + this.selectedProperty.id })
      this.$emit('breadcrumbs', breadcrumbs)
    },
    async loadPropertyData () {
      if (!this.selectedProperty) {
        this.values = []
        this.maintenanceOptions = []
        this.maintenanceEvents = []
        return
      }
      this.maintenanceTemplates = await this.$api.rpc('property', 'getPropertyMaintenanceTemplates', { propertyHolderId: this.propertyHolder.id })
      this.values = await this.$api.rpc('property', 'getPropertyValues', { propertyHolderId: this.propertyHolder.id, propertyId: this.selectedProperty.id })
      this.maintenanceOptions = await this.$api.rpc('property', 'getPropertyMaintenanceOptions', { propertyHolderId: this.propertyHolder.id, propertyId: this.selectedProperty.id })
      this.maintenanceEvents = await this.$api.rpc('property', 'getPropertyMaintenanceEvents', { propertyHolderId: this.propertyHolder.id, propertyId: this.selectedProperty.id })
    },
    formatMoney (value) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0)
    },
    propertyAddress (property) {
      const parts = [property.addressStreet1, property.addressCity, property.addressState, property.addressPostalCode].filter(Boolean)
      return parts.length > 0 ? parts.join(', ') : 'No address yet'
    },
    optionName (id) {
      const option = this.maintenanceOptions.find(item => item.option.id === id)
      return option ? option.option.name : id
    },
    formatScheduleMode (mode) {
      return MaintenanceScheduleMode[mode] || mode
    },
    openValueDialog (value) {
      this.valueDialogObject = value ? { date: value.date, value: value.value } : { date: this.todayString(), value: null }
      this.showValueDialog = true
    },
    saveValueDialog (data) {
      return this.$api.rpc('property', 'savePropertyValue', { propertyHolderId: this.propertyHolder.id, propertyId: this.selectedProperty.id, date: data.date, value: data.value })
    },
    saveTodayValue () {
      return this.$api.rpc('property', 'savePropertyValue', {
        propertyHolderId: this.propertyHolder.id,
        propertyId: this.selectedProperty.id,
        date: this.todayString(),
        value: this.todayValue
      }).then(() => {
        this.todayValue = null
        this.loadPropertyData()
      })
    },
    deleteValue (value) {
      this.$api.rpc('property', 'deletePropertyValue', { propertyHolderId: this.propertyHolder.id, propertyId: this.selectedProperty.id, date: value.date }).then(() => this.loadPropertyData())
    },
    openMaintenanceOptionDialog (option) {
      this.maintenanceOptionDialogObject = option
        ? { ...option, templateKey: null }
        : { id: null, name: '', scheduleMode: 'RESET_FROM_COMPLETION', intervalMonths: 3, startDate: this.todayString(), templateKey: null }
      this.showMaintenanceOptionDialog = true
    },
    applyPropertyTemplate (object) {
      const template = this.maintenanceTemplates.find(item => item.key === object.templateKey)
      if (!template)
        return
      object.name = template.name
      object.scheduleMode = template.scheduleMode
      object.intervalMonths = template.intervalMonths
    },
    saveMaintenanceOptionDialog (data) {
      if (data.id) {
        const payload = { ...data }
        delete payload.templateKey
        return this.$api.rpc('property', 'savePropertyMaintenanceOption', { propertyHolderId: this.propertyHolder.id, option: payload })
      }
      return this.$api.rpc('property', 'createPropertyMaintenanceOption', {
        propertyHolderId: this.propertyHolder.id,
        propertyId: this.selectedProperty.id,
        name: data.name,
        scheduleMode: data.scheduleMode,
        intervalMonths: data.intervalMonths,
        startDate: data.startDate
      })
    },
    deleteMaintenanceOption (option) {
      this.$api.rpc('property', 'deletePropertyMaintenanceOption', { propertyHolderId: this.propertyHolder.id, option }).then(() => this.loadPropertyData())
    },
    openMaintenanceEventDialog (event) {
      this.maintenanceEventDialogObject = event
        ? { ...event }
        : { id: null, date: this.todayString(), propertyMaintenanceOptionId: null, cost: null, notes: '' }
      this.showMaintenanceEventDialog = true
    },
    saveMaintenanceEventDialog (data) {
      if (data.id)
        return this.$api.rpc('property', 'savePropertyMaintenanceEvent', { propertyHolderId: this.propertyHolder.id, event: data })
      return this.$api.rpc('property', 'createPropertyMaintenanceEvent', {
        propertyHolderId: this.propertyHolder.id,
        propertyId: this.selectedProperty.id,
        propertyMaintenanceOptionId: data.propertyMaintenanceOptionId,
        date: data.date,
        cost: data.cost,
        notes: data.notes
      })
    },
    deleteMaintenanceEvent (event) {
      this.$api.rpc('property', 'deletePropertyMaintenanceEvent', { propertyHolderId: this.propertyHolder.id, event }).then(() => this.loadPropertyData())
    },
    todayString () {
      return new Date(new Date() - (new Date().getTimezoneOffset() * 60 * 1000)).toISOString().split('T')[0]
    }
  },
  watch: {
    propertyHolderId () {
      this.resubscribe()
    },
    propertyId () {
      this.propertyEditorPanel = null
      this.loadPropertyData()
      this.updateBreadcrumbs()
    }
  },
  mounted () {
    this.resubscribe()
  },
  destroyed () {
    if (this.subscription)
      this.$api.unsubscribe(this.subscription)
  }
}
</script>

<style scoped>
.summary-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
}

.property-preview {
  color: rgba(255, 255, 255, 0.8);
}

.property-edit-panel {
  margin-top: 12px;
}
</style>
